import { type CSSProperties, useState, forwardRef } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import {
  type StripeCardCvcElementOptions,
  type StripeElementChangeEvent,
  type StripeCardExpiryElementOptions,
  type StripeCardNumberElementOptions,
  type StripeElementStyle,
} from '@stripe/stripe-js';
import { useIntl } from 'react-intl';
import { clsx } from 'clsx';

import { countryAutocompleteOptions } from 'assets/countryCodes';
import { statesAutocompleteOptions } from 'assets/states';

import styles from './CardInputFields.module.scss';

const fieldStyles = {
  base: {
    fontFamily: "'Montserrat', sans-serif",
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    color: '#181F26',
    lineHeight: '24px',
    '::placeholder': {
      color: 'rgba(33, 34, 49, 0.64)',
      letterSpacing: '1.5px',
    },
  },
};

const cardFieldStyles: StripeElementStyle = {
  base: { ...fieldStyles.base, '::placeholder': { ...fieldStyles.base['::placeholder'], letterSpacing: '2.5px' } },
};

interface FieldProps {
  className?: string;
  onChange?: (event: StripeElementChangeEvent) => void;
  onFocus?: () => void;
}

const useFocus = (onFocus?: () => void) => {
  const [hasFocus, setHasFocus] = useState(false);

  const handleFocus = () => {
    setHasFocus(true);
    if (onFocus) {
      onFocus();
    }
  };
  const handleBlur = () => {
    setHasFocus(false);
  };

  return { handleFocus, handleBlur, hasFocus };
};

export const CardNumberField = ({ className, onChange, onFocus }: FieldProps) => {
  const options: StripeCardNumberElementOptions = {
    placeholder: '···· ···· ···· ····',
    style: cardFieldStyles,
    disabled: false,
  };
  const id = 'credit-card-number-input';

  const { handleFocus, handleBlur, hasFocus } = useFocus(onFocus);

  return (
    <div className={clsx(styles.fieldWrapper, hasFocus && styles.fieldWrapperFocused, className)}>
      <CardNumberElement options={options} id={id} onChange={onChange} onFocus={handleFocus} onBlur={handleBlur} />
    </div>
  );
};

export const CardExpirationField = ({ className, onChange, onFocus }: FieldProps) => {
  const intl = useIntl();

  const placeholder = intl.formatMessage({
    id: 'cardFormScreen.expirationDatePlaceholder',
    defaultMessage: 'MM / YY',
    description: 'Placeholder for the card expiration date input',
  });

  const options: StripeCardExpiryElementOptions = {
    placeholder,
    style: fieldStyles,
    disabled: false,
  };
  const id = 'credit-card-expiry-input';

  const { handleFocus, handleBlur, hasFocus } = useFocus(onFocus);

  return (
    <div className={clsx(styles.fieldWrapper, hasFocus && styles.fieldWrapperFocused, className)}>
      <CardExpiryElement options={options} id={id} onChange={onChange} onFocus={handleFocus} onBlur={handleBlur} />
    </div>
  );
};

export const CardCVCField = ({ className, onChange, onFocus }: FieldProps) => {
  const options: StripeCardCvcElementOptions = {
    placeholder: 'CVC',
    style: fieldStyles,
    disabled: false,
  };
  const id = 'credit-card-cvc-input';

  const { handleFocus, handleBlur, hasFocus } = useFocus(onFocus);

  return (
    <div className={clsx(styles.fieldWrapper, hasFocus && styles.fieldWrapperFocused, className)}>
      <CardCvcElement options={options} id={id} onChange={onChange} onFocus={handleFocus} onBlur={handleBlur} />
    </div>
  );
};

// just wanted to align inputs' styles to a single spot, not sure it's worth it :no-good:
const textFieldStyles: CSSProperties = {
  fontFamily: fieldStyles?.base?.fontFamily,
  fontStyle: fieldStyles?.base?.fontStyle,
  fontWeight: fieldStyles?.base?.fontWeight,
  fontSize: fieldStyles?.base?.fontSize,
  color: fieldStyles?.base?.color,
  lineHeight: fieldStyles?.base?.lineHeight,

  maxWidth: '100%',
};
// TODO: add placeholder styles

export const CountryField = forwardRef<HTMLSelectElement, { onChange: (newValue: string) => void }>(
  ({ onChange }, ref) => {
    const intl = useIntl();

    const placeholder = intl.formatMessage({
      id: 'cardFormScreen.countryFieldPlaceholder',
      defaultMessage: 'Country',
      description: 'Placeholder for the country input',
    });

    const [selectedCountry, setSelectedCountry] = useState('');

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedCountry(event.target.value);
      onChange(event.target.value);
    };

    return (
      // https://stackoverflow.com/a/73999430
      <div style={{ maxWidth: '100%', minWidth: 0 }}>
        <select
          required
          value={selectedCountry}
          name="country"
          autoComplete="country"
          style={textFieldStyles}
          className={clsx(styles.fieldWrapper, styles.select, styles.wideField)}
          ref={ref}
          onChange={handleChange}
        >
          <option disabled hidden value="">
            {placeholder}
          </option>
          {countryAutocompleteOptions.map(({ value, label }) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
    );
  },
);
CountryField.displayName = 'CountryField';

export const StateField = forwardRef<HTMLSelectElement, { onChange: (newValue: string) => void }>(
  ({ onChange }, ref) => {
    const intl = useIntl();

    const placeholder = intl.formatMessage({
      id: 'cardFormScreen.stateFieldPlaceholder',
      defaultMessage: 'State',
      description: 'Placeholder for the state input',
    });

    const [selectedState, setSelectedState] = useState('');

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedState(event.target.value);
      onChange(event.target.value);
    };

    return (
      <select
        required
        value={selectedState}
        name="country"
        autoComplete="country"
        style={textFieldStyles}
        className={clsx(styles.fieldWrapper, styles.select, styles.wideField)}
        ref={ref}
        onChange={handleChange}
      >
        <option disabled hidden value="">
          {placeholder}
        </option>
        {statesAutocompleteOptions.map(({ value, label }) => (
          <option value={value} key={value}>
            {label}
          </option>
        ))}
      </select>
    );
  },
);
StateField.displayName = 'StateField';

interface PlainInputProps {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
}
export const PostalCodeField = forwardRef<HTMLInputElement, PlainInputProps>(({ onChange, onFocus }, ref) => {
  const intl = useIntl();

  const placeholder = intl.formatMessage({
    id: 'cardFormScreen.postalCodeFieldPlaceholder',
    defaultMessage: 'Postal code',
    description: 'Placeholder for the postal code input',
  });

  return (
    <input
      type="text"
      autoComplete="postal-code"
      placeholder={placeholder}
      style={textFieldStyles}
      className={clsx(styles.fieldWrapper)}
      ref={ref}
      onFocus={onFocus}
      onChange={onChange}
    />
  );
});
PostalCodeField.displayName = 'PostalCodeField';
